// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { ComponentType, useMemo, useCallback } from 'react';
import { Check } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Menu, MenuItem, Typography } from '@mui/material';

export type SelectOptionProps<T> = {
  option: T;
  isSelected: boolean;
};

export type SelectProps<T> = {
  label: string;
  options: T[];
  getOptionKey: (option: T) => string;
  selectedKeys: Set<string>;
  onOptionClick: (key: string) => void;
  Option: ComponentType<SelectOptionProps<T>>;
  disabled?: boolean;
};

type SelectOptionData<T> = {
  option: T;
  key: string;
  isSelected: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-type-constraint
export const Select = <T extends any>({
  label,
  options,
  getOptionKey,
  selectedKeys,
  onOptionClick,
  Option,
  disabled,
}: SelectProps<T>) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      setAnchorEl(event.currentTarget);
    },
    [disabled],
  );

  const isOpen = Boolean(anchorEl);
  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const optionsData: SelectOptionData<T>[] = useMemo(() => {
    return options.map((option) => {
      const key = getOptionKey(option);

      return {
        option,
        key,
        isSelected: selectedKeys.has(key),
      };
    });
  }, [options, selectedKeys, getOptionKey]);

  return (
    <Box>
      <Box
        sx={[
          {
            p: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'divider',
            borderRadius: 1,
          },
          isOpen && {
            borderColor: 'secondary.main',
            borderWidth: 2,
          },
          !disabled && {
            cursor: 'pointer',
          },
        ]}
        onClick={handleClick}
      >
        <Typography variant="body1">{label}</Typography>
        {!disabled && (
          <Box sx={{ ml: 'auto', alignItems: 'center', display: 'flex' }}>
            <ArrowDropDownIcon color="action" />
          </Box>
        )}
      </Box>
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={handleClosePopover}
      >
        {optionsData.map((optionData) => (
          <MenuItem
            sx={[
              {
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '1px',
              },
              optionData.isSelected && {
                '&:not(&:hover)': {
                  backgroundColor: 'action.selected',
                },
              },
            ]}
            onClick={() => {
              onOptionClick(optionData.key);
            }}
            key={optionData.key}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <Box sx={{ maxWidth: 550, width: '100%', display: 'flex', flexDirection: 'column' }}>
                <Option option={optionData.option} isSelected={optionData.isSelected} />
              </Box>
              <Check
                sx={{
                  marginLeft: 0.25,
                  visibility: optionData.isSelected ? undefined : 'hidden',
                  color: 'inherit',
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
